// ============================================================================================
// O FORMULARIO da prateleira, separado da tela — porque agora ele tem DOIS enchedores (DA-608)
// ============================================================================================
// Ate a [DA-607] so o "Editar" preenchia este form, entao ele morava solto dentro do .tsx. Com o
// DUPLICAR ele passou a ter dois enchedores, e campo novo esquecido em um deles nao quebra nada:
// a copia so nasce sem o dado, calada. Por isso `formDoItem` e' peca UNICA e testada — o teste
// varre `Object.keys(VAZIO)`, entao campo que entrar aqui amanha e nao for copiado derruba o
// portao antes de virar defeito na tela.

import { formatMoedaSemSimbolo } from '@fiodecobre/shared';

export interface NovoProduto {
  id: number;
  codigo: string;
  descricao: string;
  marca: string | null;
  // ⛔ Nao ha unidade separada — corte do Ruan (24/08/2026): *"Se ja tem esta unidade, nao precisa
  // da outra"* ([DA-608] p.4). A `embalagem` sai da tabela `unidades_medida` e e' ela que serve de
  // unidade em todo lugar que consome a prateleira.
  embalagem: string | null;
  quantidade_embalagem: string | null;
  ncm: string | null;
  // Anotacao de onde ele viu o item e por quanto (0249) — os tres opcionais, e nenhum cria
  // identidade: quem a da e a aposentadoria, com o fornecedor DAQUELA nota.
  fornecedor_id: number | null;
  fornecedor_nome: string | null;
  codigo_fornecedor: string | null;
  preco_embalagem: string | null;
  /** CALCULADO pelo banco (preco ÷ qtd) — nao ha coluna. */
  preco_unitario: string | null;
  observacao: string | null;
  foto_url: string | null;
  status: 'ativo' | 'comprado' | 'descartado';
  encerrado_em: string | null;
  nota_entrada_id: number | null;
  criado_em: string;
  criado_por_nome: string | null;
}

export interface FormNovoProduto {
  descricao: string;
  marca: string;
  embalagem: string;
  quantidade_embalagem: string;
  ncm: string;
  observacao: string;
  fornecedor_id: number | '';
  codigo_fornecedor: string;
  preco_embalagem: string;
}

export const VAZIO: FormNovoProduto = {
  descricao: '', marca: '', embalagem: '',
  quantidade_embalagem: '', ncm: '', observacao: '',
  fornecedor_id: '', codigo_fornecedor: '', preco_embalagem: '',
};

// ---------------------------------------------------------------------------------------------
// Qtd. na embalagem — o campo fala pt-BR e o banco fala SQL, e ate 24/08/2026 ninguem traduzia
// ([DA-608] p.2, achado ao conferir o Duplicar em prod). A coluna e' `numeric(12,3)`, entao o
// Postgres devolve o numero 1 como **"1.000"**: o campo exibia isso (que se le como MIL) e o
// "Valor unitario" do mesmo modal dividia por 1000 — 0,0408/un na caixinha contra 40,7900/un na
// lista, que usa a conta do banco. Familia [DA-591]: dois lugares mostrando o mesmo dado com
// leituras diferentes, e a divergencia nao tem quem a delate. Na IDA o abismo e' o mesmo ao
// contrario — digitar "1.000" querendo mil gravava 1.
// As duas pontas passam por estas duas funcoes, que sao uma inversa da outra.
// ---------------------------------------------------------------------------------------------

/** Texto do campo (pt-BR: ponto = milhar, virgula = decimal) → numero pra mandar ao servidor. */
export function parseQtd(texto: string): number | null {
  const limpo = String(texto ?? '').trim().replace(/\./g, '').replace(',', '.');
  if (!limpo) return null;
  const n = Number(limpo);
  return Number.isFinite(n) ? n : null;
}

/** Valor como o banco devolve ("1.000" = um) → texto do campo em pt-BR ("1"). */
export function fmtQtd(valor: string | number | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '';
  const n = Number(valor);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { maximumFractionDigits: 3 }) : '';
}

/** O item gravado virando campos de tela. Serve pro Editar e pro Duplicar — sao o mesmo trabalho. */
export function formDoItem(item: NovoProduto): FormNovoProduto {
  return {
    descricao: item.descricao ?? '',
    marca: item.marca ?? '',
    embalagem: item.embalagem ?? '',
    quantidade_embalagem: fmtQtd(item.quantidade_embalagem),
    ncm: item.ncm ?? '',
    observacao: item.observacao ?? '',
    fornecedor_id: item.fornecedor_id ?? '',
    codigo_fornecedor: item.codigo_fornecedor ?? '',
    // O input de dinheiro e' pt-BR e formata no `onBlur`; entrar cru ("40.7900") apareceria
    // como outro numero e o `parseMoeda` do salvar leria pior ainda.
    preco_embalagem: item.preco_embalagem != null ? formatMoedaSemSimbolo(Number(item.preco_embalagem), 2) : '',
  };
}

/**
 * Ha alteracao nao salva na tela? Compara por CHAVE, nunca por lista escrita a mao — campo novo
 * entra sozinho. Usado so pra avisar antes de transformar uma edicao pendente em copia: o que
 * esta na tela viaja pro item NOVO e o original fica como estava, e isso precisa ser dito.
 */
export function formIgual(a: FormNovoProduto, b: FormNovoProduto): boolean {
  return (Object.keys(VAZIO) as (keyof FormNovoProduto)[]).every((k) => String(a[k] ?? '') === String(b[k] ?? ''));
}
